import { z } from "zod";
/**
 * Infers one supported strategy underlying from a recurring market title.
 *
 * @param title Human-readable venue market title.
 * @returns Supported underlying, or null when the title is ambiguous.
 */
export function inferStrategyUnderlying(title) {
    const aliases = [
        { underlying: "BTC", pattern: /\b(?:BTC|BITCOIN)\b/iu },
        { underlying: "ETH", pattern: /\b(?:ETH|ETHEREUM)\b/iu },
        { underlying: "SOL", pattern: /\b(?:SOL|SOLANA)\b/iu },
        { underlying: "XRP", pattern: /\b(?:XRP|RIPPLE)\b/iu },
        { underlying: "DOGE", pattern: /\b(?:DOGE|DOGECOIN)\b/iu },
        { underlying: "BNB", pattern: /\b(?:BNB|BINANCE COIN)\b/iu },
    ];
    return aliases.find(({ pattern }) => pattern.test(title))?.underlying ?? null;
}
/**
 * Infers a supported rolling duration from explicit title text or its clock
 * range.
 *
 * @param title Human-readable venue market title.
 * @returns Five or fifteen minutes, or null when the duration is ambiguous.
 */
export function inferRollingMarketDurationMinutes(title) {
    const explicitDuration = title.match(/\b(5|15)\s*(?:MIN|MINUTE)S?\b/iu);
    if (explicitDuration?.[1] === "5" || explicitDuration?.[1] === "15") {
        return Number(explicitDuration[1]);
    }
    const range = title.match(/\b(\d{1,2}):(\d{2})\s*(AM|PM)?\s*[-–]\s*(\d{1,2}):(\d{2})\s*(AM|PM)?\b/iu);
    if (range === null) {
        return null;
    }
    const startMinutes = clockMinutes(Number(range[1]), Number(range[2]), range[3]);
    const endMinutes = clockMinutes(Number(range[4]), Number(range[5]), range[6] ?? range[3]);
    if (startMinutes === null || endMinutes === null) {
        return null;
    }
    const difference = (endMinutes - startMinutes + 24 * 60) % (24 * 60);
    return difference === 5 || difference === 15 ? difference : null;
}
/**
 * Converts one clock fragment into minutes after midnight.
 *
 * @param hour Clock hour.
 * @param minute Clock minute.
 * @param meridiem Optional AM/PM marker.
 * @returns Minutes after midnight, or null for an invalid clock.
 */
function clockMinutes(hour, minute, meridiem) {
    if (!Number.isInteger(hour) ||
        !Number.isInteger(minute) ||
        minute < 0 ||
        minute > 59) {
        return null;
    }
    if (meridiem === undefined) {
        return hour >= 0 && hour <= 23 ? hour * 60 + minute : null;
    }
    if (hour < 1 || hour > 12) {
        return null;
    }
    const normalizedHour = hour % 12 + (meridiem.toUpperCase() === "PM" ? 12 : 0);
    return normalizedHour * 60 + minute;
}
/** Default L2 strategy submitted by the Backtests page and production smoke test. */
export const DEFAULT_BACKTEST_STRATEGY_V2 = {
    schemaVersion: 2,
    name: "BTC 5m L2 pressure",
    description: "Buy the leading BTC five-minute outcome late when persistent depth and order flow imply enough net executable edge.",
    universe: {
        families: [
            { venue: "polymarket", underlying: "BTC", durationMinutes: 5 },
        ],
    },
    entry: {
        anchor: "market_close",
        startOffsetSeconds: -30,
        endOffsetSeconds: -2,
        outcomeSelection: "leading",
        signals: [
            {
                id: "depth-pressure",
                weight: 0.45,
                signal: {
                    type: "depth_imbalance",
                    depth: { mode: "levels", levels: 5 },
                },
            },
            {
                id: "order-flow",
                weight: 0.35,
                signal: {
                    type: "order_flow_imbalance",
                    lookbackMilliseconds: 1_000,
                    depth: { mode: "levels", levels: 5 },
                },
            },
            {
                id: "ephemeral-penalty",
                weight: 0.2,
                signal: {
                    type: "ephemeral_liquidity_score",
                    lookbackMilliseconds: 3_000,
                    maximumRestingMilliseconds: 1_000,
                    depth: { mode: "levels", levels: 5 },
                },
            },
        ],
        minimumScorePercent100: 30,
        minimumScorePersistenceMilliseconds: 750,
        referenceAgreement: {
            source: "chainlink",
            lookbackMilliseconds: 1_000,
            minimumMomentumPercent100: 0.01,
        },
        maximumBookAgeMilliseconds: 1_000,
        maximumEphemeralLiquidityPenaltyPercent100: 40,
        expectedMoveAtFullScorePercent1: 0.05,
        minimumExpectedNetEdgePercent100: 0.25,
        order: {
            type: "market",
            limitPricePercent1: null,
            sizing: "fixed_dollars",
            size: 10,
        },
        maxEntriesPerMarket: 1,
    },
    execution: {
        latencyMilliseconds: 250,
        displayedDepthParticipationPercent100: 30,
        maximumSlippagePercent100: 2,
        expectedEdgeSlippageBufferPercent100: 0.25,
    },
    exit: {
        takeProfitPercent100: 10,
        stopLossPercent100: 5,
        maximumHoldingSeconds: 30,
        signalReversalScorePercent100: -10,
        pressureDecayScorePercent100: 5,
        exitBeforeCloseSeconds: 1,
    },
    portfolio: {
        maxConcurrentPositions: 3,
        maxPositionDollars: 25,
    },
};
/** Email and password fields shared by registration and login. */
const credentialsRequestSchema = z.object({
    email: z.email().transform((email) => email.trim().toLowerCase()),
    password: z.string().min(12).max(200),
});
/** Registration payload with the required 13+ eligibility acknowledgement. */
export const registerRequestSchema = credentialsRequestSchema.extend({
    eligibilityAcknowledged: z.literal(true),
});
/** Login payload. */
export const loginRequestSchema = credentialsRequestSchema;
/** Email confirmation payload. */
export const confirmEmailRequestSchema = z.object({
    token: z.string().min(20).max(300),
});
/** Public support, feedback, privacy, and security contact payload. */
export const contactRequestSchema = z.object({
    name: z.string().trim().min(2).max(120),
    email: z.string().trim().pipe(z.email()).transform((email) => email.toLowerCase()),
    topic: z.enum(["support", "feedback", "account", "privacy", "security"]),
    message: z.string().trim().min(20).max(5_000),
    website: z.string().trim().max(200).optional().default(""),
});
/** Wallet creation payload. */
export const createWalletRequestSchema = z.object({
    name: z.string().trim().min(1).max(80),
    startingBalanceDollars: z.number().min(10).max(10_000_000),
});
/** Wallet update payload. */
export const updateWalletRequestSchema = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    config: z
        .object({
        currency: z.literal("USD"),
        maxSlippagePercent100: z.number().min(0).max(100),
        feeOverrides: z.record(z.enum(["polymarket", "kalshi"]), z.number().min(0).max(1)),
    })
        .optional(),
});
/** Admin-managed application settings payload. */
export const updateApplicationSettingsRequestSchema = z.object({
    freeWalletLimit: z.number().int().min(1).max(1_000),
    trialWalletLimit: z.number().int().min(1).max(1_000),
    paidWalletLimit: z.number().int().min(1).max(1_000),
    trialDurationDays: z.number().int().min(1).max(365),
});
/** Administrator-created account payload. The user confirms ownership by email. */
export const createAdminUserRequestSchema = credentialsRequestSchema.extend({
    role: z.enum(["admin", "user"]).default("user"),
});
/** Administrator-only mutable account fields. Email addresses remain immutable. */
export const updateAdminUserRequestSchema = z
    .object({
    password: z.string().min(12).max(200).optional(),
    role: z.enum(["admin", "user"]).optional(),
})
    .refine((value) => value.password !== undefined || value.role !== undefined, {
    message: "Provide a password or role.",
});
/** Authenticated self-service password change payload. */
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(12).max(200),
    newPassword: z.string().min(12).max(200),
});
/** Authenticated account-deletion confirmation payload. */
export const deleteAccountRequestSchema = z.object({
    password: z.string().min(12).max(200),
});
/** Optional wallet selector used by API-key default-wallet routes. */
export const walletSelectorQuerySchema = z.object({
    walletId: z.uuid().optional(),
    walletName: z.string().trim().min(1).max(80).optional(),
});
/** Market search query. */
export const marketSearchQuerySchema = z.object({
    q: z.string().trim().min(2).max(120),
    venue: z.enum(["all", "polymarket", "kalshi"]).default("all"),
});
/** Query for resolving a supported public venue market URL. */
export const marketUrlResolveQuerySchema = z.object({
    url: z.url().max(2_048),
});
/** Paper order payload using canonical venue instrument identifiers. */
export const placeOrderRequestSchema = z.object({
    walletId: z.uuid().optional(),
    walletName: z.string().trim().min(1).max(80).optional(),
    venue: z.enum(["polymarket", "kalshi"]),
    marketId: z.string().min(1).max(300),
    outcomeId: z.string().min(1).max(500),
    side: z.enum(["buy", "sell"]),
    quantity: z.number().positive().max(1_000_000).optional(),
    maxSpendDollars: z.number().positive().max(10_000_000).optional(),
    type: z.enum(["market", "limit"]).default("market"),
    timeInForce: z.enum(["fok", "ioc", "gtc"]).optional(),
    limitPricePercent1: z.number().min(0.001).max(0.999).optional(),
    executionFloorPricePercent1: z.number().min(0.001).max(0.999).optional(),
    clientOrderId: z.string().trim().min(1).max(500).optional(),
    maxSlippagePercent100: z.number().min(0).max(100).optional(),
}).superRefine((value, context) => {
    if (value.walletId !== undefined && value.walletName !== undefined) {
        context.addIssue({
            code: "custom",
            message: "Supply walletId or walletName, not both.",
            path: ["walletId"],
        });
    }
    if ((value.quantity === undefined) ===
        (value.maxSpendDollars === undefined)) {
        context.addIssue({
            code: "custom",
            message: "Supply exactly one of quantity or maxSpendDollars.",
            path: ["quantity"],
        });
    }
    if (value.side === "sell" && value.maxSpendDollars !== undefined) {
        context.addIssue({
            code: "custom",
            message: "maxSpendDollars is available only for buy orders.",
            path: ["maxSpendDollars"],
        });
    }
    if (value.type === "limit" && value.limitPricePercent1 === undefined) {
        context.addIssue({
            code: "custom",
            message: "A limit order requires limitPricePercent1.",
            path: ["limitPricePercent1"],
        });
    }
    if (value.type === "market" && value.limitPricePercent1 !== undefined) {
        context.addIssue({
            code: "custom",
            message: "A market order cannot include limitPricePercent1.",
            path: ["limitPricePercent1"],
        });
    }
    if (value.side !== "buy" && value.executionFloorPricePercent1 !== undefined) {
        context.addIssue({
            code: "custom",
            message: "executionFloorPricePercent1 is available only for buy orders.",
            path: ["executionFloorPricePercent1"],
        });
    }
    if (value.type === "limit" &&
        value.executionFloorPricePercent1 !== undefined &&
        value.limitPricePercent1 !== undefined &&
        value.executionFloorPricePercent1 > value.limitPricePercent1) {
        context.addIssue({
            code: "custom",
            message: "executionFloorPricePercent1 cannot exceed limitPricePercent1.",
            path: ["executionFloorPricePercent1"],
        });
    }
    if (value.type === "market" && value.timeInForce !== undefined && value.timeInForce !== "fok") {
        context.addIssue({
            code: "custom",
            message: "Market orders use fill-or-kill time in force.",
            path: ["timeInForce"],
        });
    }
});
/** Order list query with bounded keyset pagination. */
export const orderListQuerySchema = z.object({
    walletId: z.uuid().optional(),
    walletName: z.string().trim().min(1).max(80).optional(),
    status: z.enum([
        "open",
        "closed",
        "all",
        "accepted",
        "partially_filled",
        "filled",
        "canceled",
        "expired",
        "rejected",
    ]).default("all"),
    venue: z.enum(["polymarket", "kalshi"]).optional(),
    marketId: z.string().min(1).max(300).optional(),
    side: z.enum(["buy", "sell"]).optional(),
    start: z.iso.datetime().optional(),
    end: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().max(500).optional(),
});
/** Position list query. */
export const positionListQuerySchema = z.object({
    status: z.enum(["open", "closed", "all"]).default("open"),
    start: z.iso.datetime().optional(),
    end: z.iso.datetime().optional(),
});
/** Position-close query. */
export const positionCloseQuerySchema = z.object({
    maxSlippagePercent100: z.coerce.number().min(0).max(100).optional(),
});
/** Order replacement payload. */
export const replaceOrderRequestSchema = z.object({
    quantity: z.number().positive().max(1_000_000).optional(),
    limitPricePercent1: z.number().min(0.001).max(0.999).optional(),
    executionFloorPricePercent1: z.number().min(0.001).max(0.999).optional(),
}).refine((value) => value.quantity !== undefined ||
    value.limitPricePercent1 !== undefined ||
    value.executionFloorPricePercent1 !== undefined, { message: "Supply quantity, limitPricePercent1, or executionFloorPricePercent1." });
/** One ordered bulk order mutation. */
export const bulkOrderActionSchema = z.discriminatedUnion("action", [
    z.object({
        action: z.literal("place"),
        idempotencyKey: z.string().trim().min(1).max(200).optional(),
        order: placeOrderRequestSchema,
    }),
    z.object({
        action: z.literal("replace"),
        orderId: z.uuid(),
        order: replaceOrderRequestSchema,
    }),
    z.object({
        action: z.literal("cancel"),
        orderId: z.uuid(),
    }),
]);
/** Bounded ordered bulk order mutation payload. */
export const bulkOrderRequestSchema = z.object({
    actions: z.array(bulkOrderActionSchema).min(1).max(50),
});
/** Portfolio-history query. */
export const portfolioHistoryQuerySchema = z.object({
    start: z.iso.datetime().optional(),
    end: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(2_000).default(500),
});
/** Account-activity query. */
export const accountActivityQuerySchema = z.object({
    type: z.enum(["fill", "settlement", "reset"]).optional(),
    start: z.iso.datetime().optional(),
    end: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().max(500).optional(),
});
/** Shared venue and market filters for historical market-data queries. */
export const historicalMarketDataQuerySchema = z.object({
    venue: z.enum(["polymarket", "kalshi"]).optional(),
    marketId: z.string().trim().min(1).max(500).optional(),
    outcomeId: z.string().trim().min(1).max(500).optional(),
    start: z.iso.datetime(),
    end: z.iso.datetime(),
    limit: z.coerce.number().int().min(1).max(10_000).default(1_000),
    cursor: z.string().max(1_000).optional(),
});
/** Historical market-definition list query. */
export const historicalMarketListQuerySchema = z.object({
    venue: z.enum(["polymarket", "kalshi"]).optional(),
    marketId: z.string().trim().min(1).max(500).optional(),
    asOf: z.iso.datetime().optional(),
    closesAtStart: z.iso.datetime().optional(),
    closesAtEnd: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(10_000).default(1_000),
    cursor: z.string().max(1_000).optional(),
}).superRefine((query, context) => {
    if (query.closesAtStart !== undefined &&
        query.closesAtEnd !== undefined &&
        Date.parse(query.closesAtStart) > Date.parse(query.closesAtEnd)) {
        context.addIssue({
            code: "custom",
            path: ["closesAtEnd"],
            message: "Close-time end must be at or after close-time start.",
        });
    }
});
/** Point-in-time market-definition query. */
export const historicalMarketDefinitionQuerySchema = z.object({
    asOf: z.iso.datetime().optional(),
});
/** Point-in-time replayable order-book query. */
export const historicalBookQuerySchema = z.object({
    at: z.iso.datetime(),
});
/** Bounded Parquet export creation payload. */
export const createMarketDataExportRequestSchema = z.object({
    dataset: z.enum(["markets", "trades", "quotes", "candles", "books"]),
    venue: z.enum(["polymarket", "kalshi"]).optional(),
    marketId: z.string().trim().min(1).max(500).optional(),
    outcomeId: z.string().trim().min(1).max(500).optional(),
    start: z.iso.datetime(),
    end: z.iso.datetime(),
});
const strategyComparatorSchema = z.enum(["gt", "gte", "lt", "lte"]);
const strategyConditionSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("price"),
        field: z.enum(["bid", "ask", "midpoint"]),
        operator: strategyComparatorSchema,
        valuePercent1: z.number().min(0).max(1),
    }),
    z.object({
        type: z.literal("trend"),
        lookbackSeconds: z.number().int().min(1).max(86_400),
        operator: strategyComparatorSchema,
        returnPercent100: z.number().min(-100).max(10_000),
    }),
    z.object({
        type: z.literal("book_imbalance"),
        operator: strategyComparatorSchema,
        imbalancePercent100: z.number().min(-100).max(100),
    }),
    z.object({
        type: z.literal("reference_direction"),
        source: z.enum(["chainlink", "binance"]),
        minimumMovePercent100: z.number().min(0).max(100),
    }),
]);
const strategyDepthScopeV2Schema = z.discriminatedUnion("mode", [
    z.object({
        mode: z.literal("levels"),
        levels: z.number().int().min(1).max(100),
    }),
    z.object({
        mode: z.literal("price_distance"),
        distancePercent100: z.number().positive().max(100),
    }),
]);
const strategySignalV2Schema = z.discriminatedUnion("type", [
    ...strategyConditionSchema.options,
    z.object({
        type: z.literal("depth_imbalance"),
        depth: strategyDepthScopeV2Schema,
    }),
    z.object({
        type: z.literal("order_flow_imbalance"),
        lookbackMilliseconds: z.union([
            z.literal(250),
            z.literal(1_000),
            z.literal(3_000),
        ]),
        depth: strategyDepthScopeV2Schema,
    }),
    z.object({
        type: z.literal("aggressive_trade_imbalance"),
        lookbackMilliseconds: z.union([
            z.literal(250),
            z.literal(1_000),
            z.literal(3_000),
            z.literal(10_000),
        ]),
    }),
    z.object({
        type: z.literal("microprice_deviation"),
    }),
    z.object({
        type: z.literal("liquidity_persistence"),
        lookbackMilliseconds: z.union([
            z.literal(250),
            z.literal(1_000),
            z.literal(3_000),
            z.literal(10_000),
        ]),
        depth: strategyDepthScopeV2Schema,
    }),
    z.object({
        type: z.literal("book_resilience"),
        lookbackMilliseconds: z.union([
            z.literal(250),
            z.literal(1_000),
            z.literal(3_000),
            z.literal(10_000),
        ]),
        depth: strategyDepthScopeV2Schema,
    }),
    z.object({
        type: z.literal("ephemeral_liquidity_score"),
        lookbackMilliseconds: z.union([
            z.literal(250),
            z.literal(1_000),
            z.literal(3_000),
            z.literal(10_000),
        ]),
        maximumRestingMilliseconds: z.number().int().min(1).max(10_000),
        depth: strategyDepthScopeV2Schema,
    }),
    z.object({
        type: z.literal("reference_momentum"),
        source: z.enum(["chainlink", "binance"]),
        lookbackMilliseconds: z.union([
            z.literal(250),
            z.literal(1_000),
            z.literal(3_000),
            z.literal(10_000),
        ]),
    }),
    z.object({
        type: z.literal("reference_target_distance"),
        source: z.enum(["chainlink", "binance"]),
        normalizationLookbackMilliseconds: z.union([
            z.literal(1_000),
            z.literal(3_000),
            z.literal(10_000),
            z.literal(60_000),
        ]),
    }),
]);
/** Validated version-one strategy definition. */
export const strategyDefinitionV1Schema = z.object({
    schemaVersion: z.literal(1),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(2_000),
    universe: z.object({
        families: z
            .array(z.object({
            venue: z.enum(["polymarket", "kalshi"]),
            underlying: z.enum(["BTC", "ETH", "SOL", "XRP", "DOGE", "BNB"]),
            durationMinutes: z.union([z.literal(5), z.literal(15)]),
        }))
            .min(1)
            .max(24),
    }),
    entry: z.object({
        anchor: z.enum(["market_open", "market_close"]),
        startOffsetSeconds: z.number().int().min(-86_400).max(86_400),
        endOffsetSeconds: z.number().int().min(-86_400).max(86_400),
        outcomeSelection: z.enum(["up", "down", "leading", "reference_direction"]),
        conditionMatch: z.enum(["all", "any"]),
        conditions: z.array(strategyConditionSchema).max(12),
        order: z.object({
            type: z.enum(["market", "limit"]),
            limitPricePercent1: z.number().min(0).max(1).nullable(),
            sizing: z.enum(["fixed_contracts", "fixed_dollars"]),
            size: z.number().positive().max(1_000_000),
        }),
        maxEntriesPerMarket: z.number().int().min(1).max(20),
    }),
    exit: z.object({
        takeProfitPercent100: z.number().positive().max(10_000).nullable(),
        stopLossPercent100: z.number().positive().max(100).nullable(),
        exitBeforeCloseSeconds: z.number().int().min(0).max(86_400),
    }),
    portfolio: z.object({
        maxConcurrentPositions: z.number().int().min(1).max(1_000),
        maxPositionDollars: z.number().positive().max(100_000_000),
    }),
}).superRefine((definition, context) => {
    if (definition.entry.startOffsetSeconds > definition.entry.endOffsetSeconds) {
        context.addIssue({
            code: "custom",
            path: ["entry", "endOffsetSeconds"],
            message: "End offset must be greater than or equal to start offset.",
        });
    }
    if (definition.entry.order.type === "limit" &&
        definition.entry.order.limitPricePercent1 === null) {
        context.addIssue({
            code: "custom",
            path: ["entry", "order", "limitPricePercent1"],
            message: "A limit order requires a limit price.",
        });
    }
});
/** Validated version-two L2-aware strategy definition. */
export const strategyDefinitionV2Schema = z.object({
    schemaVersion: z.literal(2),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(2_000),
    universe: z.object({
        families: z
            .array(z.object({
            venue: z.enum(["polymarket", "kalshi"]),
            underlying: z.enum(["BTC", "ETH", "SOL", "XRP", "DOGE", "BNB"]),
            durationMinutes: z.union([z.literal(5), z.literal(15)]),
        }))
            .min(1)
            .max(24),
    }),
    entry: z.object({
        anchor: z.enum(["market_open", "market_close"]),
        startOffsetSeconds: z.number().int().min(-86_400).max(86_400),
        endOffsetSeconds: z.number().int().min(-86_400).max(86_400),
        outcomeSelection: z.enum(["up", "down", "leading", "reference_direction"]),
        signals: z
            .array(z.object({
            id: z.string().trim().min(1).max(80),
            weight: z.number().min(0).max(1_000),
            signal: strategySignalV2Schema,
        }))
            .min(1)
            .max(24),
        minimumScorePercent100: z.number().min(-100).max(100),
        minimumScorePersistenceMilliseconds: z
            .number()
            .int()
            .min(0)
            .max(60_000)
            .optional(),
        referenceAgreement: z
            .object({
            source: z.enum(["chainlink", "binance"]),
            lookbackMilliseconds: z.union([
                z.literal(250),
                z.literal(1_000),
                z.literal(3_000),
                z.literal(10_000),
            ]),
            minimumMomentumPercent100: z.number().min(0).max(100),
        })
            .nullable()
            .optional(),
        maximumBookAgeMilliseconds: z
            .number()
            .int()
            .positive()
            .max(60_000)
            .nullable()
            .optional(),
        maximumEphemeralLiquidityPenaltyPercent100: z
            .number()
            .min(0)
            .max(100)
            .nullable()
            .optional(),
        expectedMoveAtFullScorePercent1: z.number().min(0).max(1),
        minimumExpectedNetEdgePercent100: z.number().min(-100).max(10_000),
        order: z.object({
            type: z.enum(["market", "limit"]),
            limitPricePercent1: z.number().min(0).max(1).nullable(),
            sizing: z.enum(["fixed_contracts", "fixed_dollars"]),
            size: z.number().positive().max(1_000_000),
        }),
        maxEntriesPerMarket: z.number().int().min(1).max(20),
    }),
    execution: z.object({
        latencyMilliseconds: z.number().int().min(0).max(60_000),
        displayedDepthParticipationPercent100: z.number().positive().max(100),
        maximumSlippagePercent100: z.number().min(0).max(10_000).nullable(),
        expectedEdgeSlippageBufferPercent100: z
            .number()
            .min(0)
            .max(100)
            .optional(),
    }),
    exit: z.object({
        takeProfitPercent100: z.number().positive().max(10_000).nullable(),
        stopLossPercent100: z.number().positive().max(100).nullable(),
        maximumHoldingSeconds: z.number().positive().max(86_400).nullable(),
        signalReversalScorePercent100: z.number().min(-100).max(100).nullable(),
        pressureDecayScorePercent100: z.number().min(-100).max(100).nullable(),
        exitBeforeCloseSeconds: z.number().int().min(0).max(86_400),
    }),
    portfolio: z.object({
        maxConcurrentPositions: z.number().int().min(1).max(1_000),
        maxPositionDollars: z.number().positive().max(100_000_000),
    }),
}).superRefine((definition, context) => {
    if (definition.entry.startOffsetSeconds > definition.entry.endOffsetSeconds) {
        context.addIssue({
            code: "custom",
            path: ["entry", "endOffsetSeconds"],
            message: "End offset must be greater than or equal to start offset.",
        });
    }
    if (definition.entry.order.type === "limit" &&
        definition.entry.order.limitPricePercent1 === null) {
        context.addIssue({
            code: "custom",
            path: ["entry", "order", "limitPricePercent1"],
            message: "A limit order requires a limit price.",
        });
    }
    if (definition.entry.signals.every((component) => component.weight === 0)) {
        context.addIssue({
            code: "custom",
            path: ["entry", "signals"],
            message: "At least one signal must have a positive weight.",
        });
    }
    const ids = new Set();
    for (const [index, component] of definition.entry.signals.entries()) {
        if (ids.has(component.id)) {
            context.addIssue({
                code: "custom",
                path: ["entry", "signals", index, "id"],
                message: "Signal identifiers must be unique.",
            });
        }
        ids.add(component.id);
        if (component.signal.type === "ephemeral_liquidity_score" &&
            component.signal.maximumRestingMilliseconds >
                component.signal.lookbackMilliseconds) {
            context.addIssue({
                code: "custom",
                path: [
                    "entry",
                    "signals",
                    index,
                    "signal",
                    "maximumRestingMilliseconds",
                ],
                message: "Maximum resting time cannot exceed the signal lookback.",
            });
        }
    }
});
/** Backward-compatible validated strategy definition. */
export const strategyDefinitionSchema = z.discriminatedUnion("schemaVersion", [
    strategyDefinitionV1Schema,
    strategyDefinitionV2Schema,
]);
/** Natural-language strategy-generation payload. */
export const generateStrategyRequestSchema = z.object({
    prompt: z.string().trim().min(3).max(4_000),
});
/** Synchronous bounded backtest request. */
export const createBacktestRequestSchema = z
    .object({
    strategy: strategyDefinitionSchema,
    start: z.iso.datetime(),
    end: z.iso.datetime(),
    initialCapitalDollars: z.number().positive().max(100_000_000),
})
    .superRefine((request, context) => {
    if (Date.parse(request.start) >= Date.parse(request.end)) {
        context.addIssue({
            code: "custom",
            path: ["end"],
            message: "Backtest end must be after its start.",
        });
    }
    if (Date.parse(request.end) - Date.parse(request.start) > 31 * 86_400_000) {
        context.addIssue({
            code: "custom",
            path: ["end"],
            message: "Interactive backtests may cover at most 31 days.",
        });
    }
});
/** API-key creation payload. */
export const createApiKeyRequestSchema = z.object({
    name: z.string().trim().min(1).max(80),
    walletId: z.uuid(),
    access: z.enum(["wallet", "all"]).optional(),
    level: z.enum(["wallet", "account"]).optional(),
}).superRefine((value, context) => {
    if (value.level === "account" && value.access === "wallet") {
        context.addIssue({
            code: "custom",
            path: ["access"],
            message: "Account-level API keys require all-wallet access.",
        });
    }
});
/** API-key authorization update payload. */
export const updateApiKeyRequestSchema = z
    .object({
    access: z.enum(["wallet", "all"]).optional(),
    defaultWalletId: z.uuid().optional(),
    level: z.enum(["wallet", "account"]).optional(),
})
    .superRefine((value, context) => {
    if (value.level === "account" && value.access === "wallet") {
        context.addIssue({
            code: "custom",
            path: ["access"],
            message: "Account-level API keys require all-wallet access.",
        });
    }
})
    .refine((value) => value.access !== undefined ||
    value.defaultWalletId !== undefined ||
    value.level !== undefined, { message: "Provide access, defaultWalletId, or level." });
/** Validated experiment creation payload. */
export const createExperimentRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    hypothesis: z.string().trim().min(1).max(2_000),
    description: z.string().trim().max(10_000).nullable().default(null),
    successCriteria: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
    guardrails: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
    parentExperimentId: z.uuid().nullable().default(null),
});
/** Validated immutable experiment strategy-version payload. */
export const createExperimentStrategyVersionRequestSchema = z.object({
    definition: strategyDefinitionSchema,
    parentVersionId: z.uuid().nullable().default(null),
    changeNote: z.string().trim().min(1).max(2_000),
    engineVersion: z.string().trim().min(1).max(80),
});
/** Validated experiment-run creation payload. */
export const createExperimentRunRequestSchema = z.object({
    strategyVersionId: z.uuid(),
    walletId: z.uuid().nullable().default(null),
    mode: z.literal("external_paper"),
    startAt: z.iso.datetime().optional(),
    endAt: z.iso.datetime().nullable().default(null),
    engineVersion: z.string().trim().min(1).max(80),
    configuration: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
}).superRefine((value, context) => {
    if (value.startAt !== undefined &&
        value.endAt !== null &&
        Date.parse(value.startAt) >= Date.parse(value.endAt)) {
        context.addIssue({
            code: "custom",
            path: ["endAt"],
            message: "Run endAt must be after startAt.",
        });
    }
});
/** Validated batch decision-ingestion event. */
export const decisionEventInputSchema = z.object({
    eventKey: z.string().trim().min(1).max(200),
    signalId: z.string().trim().min(1).max(200).nullable().default(null),
    type: z.enum([
        "candidate",
        "rejected",
        "qualified",
        "order_submitted",
        "order_filled",
        "invalidated",
        "exit",
    ]),
    phase: z.enum(["entry", "exit"]),
    venue: z.enum(["polymarket", "kalshi"]),
    marketId: z.string().trim().min(1).max(500),
    outcomeId: z.string().trim().min(1).max(500).nullable().default(null),
    reason: z.string().trim().min(1).max(200).nullable().default(null),
    sourceAt: z.iso.datetime().nullable().default(null),
    observedAt: z.iso.datetime(),
    features: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
    thresholds: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
    evidence: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
    orderId: z.uuid().nullable().default(null),
    positionId: z.uuid().nullable().default(null),
    economics: z.object({
        grossProfitDollars: z.number().finite().nullable().default(null),
        feesDollars: z.number().nonnegative().finite().nullable().default(null),
        netProfitDollars: z.number().finite().nullable().default(null),
    }).default({
        grossProfitDollars: null,
        feesDollars: null,
        netProfitDollars: null,
    }),
});
/** Validated retry-safe decision-event batch. */
export const ingestDecisionEventsRequestSchema = z.object({
    events: z.array(decisionEventInputSchema).min(1).max(500),
});
//# sourceMappingURL=index.js.map